import { faCircleMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Button, IconButton } from "@ventumsoftware/react-toolkit";
import React, { type FC, useState } from "react";
import { useApiCall, ValidationError } from "../api/api";
import type { Inspection } from "../api/dto/Inspection";
import { LoadCapacity } from "../api/dto/LoadCapacity";
import { Input } from "../components/form/Input";
import { Select } from "../components/form/Select";
import { ModalFooter } from "../components/layout/ModalProvider";
import { LiftingEquipment } from "../api/dto/LiftingEquipment";

const parseHijsfirmas = (value: string | undefined): string[] => {
  const parsed = (value ?? "")
    .split(",")
    .map((f) => f.trim())
    .filter((f) => f !== "");
  return parsed.length > 0 ? parsed : [""];
};
const weatherRainfallOptions = {
  Droog: "Droog",
  Wisselvallig: "Wisselvallig",
  Regen: "Regen",
  Sneeuw: "Sneeuw",
  Hagel: "Hagel",
  "Onweer/Regen": "Onweer/Regen",
  "Onweer/Hagel": "Onweer/Hagel",
};
const weatherCloudsOptions = {
  "Niet bewolkt": "Niet bewolkt",
  "Half bewolkt": "Half bewolkt",
  Bewolkt: "Bewolkt",
};
const weatherSightOptions = {
  "Vrij zicht": "Vrij zicht",
  "Beperkt zicht": "Beperkt zicht",
};

// TODO: for now this is static, should be dynamic later on.
const liftingEquipmentTypeOptions = {
  "-": "Selecteer type",
  Autolaadkraan: "Autolaadkraan",
  "Mobiele Hijskraan": "Mobiele Hijskraan",
  "Vaste Torenkraan": "Vaste Torenkraan",
  "Mobiele Torenkraan": "Mobiele Torenkraan",
  Rupskraan: "Rupskraan",
  "Sleen / Vijzelen": "Sleën / Vijzelen",
  "Kettingtakel (vast hijspunt)": "Kettingtakel (vast hijspunt)",
  "Hijskraan op ponton": "Hijskraan op ponton",
  Kraanschip: "Kraanschip",
};
const getLiftingEquipmentSizeUnitByType: (type: keyof typeof liftingEquipmentTypeOptions) => string = (type) => {
  switch (type) {
    case "Mobiele Torenkraan":
    case "Vaste Torenkraan":
      return "meter";
    default:
      return "Tons";
  }
};

export const EditInspectionDetailsModal: FC<{ inspection: Inspection; onFinish?: () => void }> = (props) => {
  const { editInspectionExtraFields } = useApiCall();
  const [firms, setFirms] = useState<string[]>(() =>
    parseHijsfirmas(props.inspection.extraFieldsFormatted["Hijsfirma"]),
  );
  const [weatherRainfall, setWeatherRainfall] = useState<string>(
    Object.keys(weatherRainfallOptions).includes(props.inspection.extraFields["weather_rainfall"])
      ? props.inspection.extraFields["weather_rainfall"]
      : Object.keys(weatherRainfallOptions)[0],
  );
  const [weatherClouds, setWeatherClouds] = useState<string>(
    Object.keys(weatherCloudsOptions).includes(props.inspection.extraFields["weather_clouds"])
      ? props.inspection.extraFields["weather_clouds"]
      : Object.keys(weatherCloudsOptions)[0],
  );
  const [weatherSight, setWeatherSight] = useState<string>(
    Object.keys(weatherSightOptions).includes(props.inspection.extraFields["weather_sight"])
      ? props.inspection.extraFields["weather_sight"]
      : Object.keys(weatherSightOptions)[0],
  );
  const [weatherTemp, setWeatherTemp] = useState<string>(props.inspection.extraFields["weather_temp"] ?? "");
  const [weatherSpeed, setWeatherSpeed] = useState<string>(props.inspection.extraFields["weather_wind_speed"] ?? "");
  const [weatherMeasureHeight, setWeatherMeasureHeight] = useState<string>(
    props.inspection.extraFields["weather_measure_height"] ?? "10",
  );
  const [material, setMaterial] = useState<string>(props.inspection.extraFields["Materieel"] ?? "");
  const [loadCapacities, setLoadCapacities] = useState<LoadCapacity[]>(props.inspection.loadCapacities ?? []);
  const [liftingEquipments, setLiftingEquipments] = useState<LiftingEquipment[]>(
    props.inspection.liftingEquipments ?? [],
  );

  const [jobNumber, setJobNumber] = useState<string>(props.inspection.inspectionNumber ?? "");

  const [errors, setErrors] = useState<string[]>([]);

  const save = async () => {
    await editInspectionExtraFields(
      props.inspection,
      {
        Hijsfirma: firms
          .map((f) => f.trim())
          .filter((f) => f !== "")
          .join(", "),
        weather_rainfall: weatherRainfall,
        weather_clouds: weatherClouds,
        weather_sight: weatherSight,
        weather_temp: weatherTemp,
        weather_wind_speed: weatherSpeed,
        weather_measure_height: weatherMeasureHeight,
      },
      loadCapacities,
      liftingEquipments,
      jobNumber.trim(),
    ).catch((e) => {
      if (e instanceof ValidationError) {
        setErrors(Object.values(e.errors).flat());
      }
      throw e;
    });
    props.onFinish?.();
  };
  return (
    <div className={""}>
      <table className={"w-full"}>
        <tbody>
          <tr>
            <td className={"font-medium pr-3 py-3 align-top"}>Job Number:</td>
            <td className={"pb-3"}>
              <Input type={"text"} label={""} value={jobNumber} onChange={setJobNumber} />
            </td>
          </tr>
          {firms.map((firmValue, index) => (
            <tr key={index}>
              <td className={"font-medium pr-3 py-3 align-top"}>{index === 0 ? "Hijsfirma:" : ""}</td>
              <td className={"pb-3"}>
                <div className={"flex items-center space-x-2"}>
                  <Input
                    type={"text"}
                    label={""}
                    value={firmValue}
                    onChange={(value) => setFirms((prev) => prev.map((f, i) => (i === index ? value : f)))}
                  />
                  {firms.length > 1 && (
                    <IconButton
                      size={"md"}
                      icon={faCircleMinus}
                      type={"danger"}
                      onClick={() => setFirms((prev) => prev.filter((_, i) => i !== index))}
                    />
                  )}
                </div>
              </td>
            </tr>
          ))}
          <tr>
            <td className={"font-medium pr-3 py-3 align-top"}></td>
            <td className={"pb-6"}>
              <Button
                type={"secondary"}
                icon={faPlus}
                size={"sm"}
                text={"extra hijsfirma"}
                onClick={() => setFirms((prev) => [...prev, ""])}
              />
            </td>
          </tr>
          <tr>
            <td className={"font-medium pr-3 py-3 align-top"}>Weercondities:</td>
            <td className={"pb-6"}>
              <div className={"flex flex-wrap items-center space-x-2 text-sm font-medium text-brand-800"}>
                <div className={"w-32"}>
                  <Select
                    options={weatherRainfallOptions}
                    label={""}
                    value={weatherRainfall}
                    onChange={setWeatherRainfall}
                  />
                </div>
                <div> / </div>
                <div className={"w-32"}>
                  <Select options={weatherCloudsOptions} label={""} value={weatherClouds} onChange={setWeatherClouds} />
                </div>
                <div> / </div>
                <div className={"w-32"}>
                  <Select options={weatherSightOptions} label={""} value={weatherSight} onChange={setWeatherSight} />
                </div>
                <div> / </div>
                <div className={"w-32"}>
                  <Input
                    type={"number"}
                    label={""}
                    value={parseInt(weatherTemp)}
                    onChange={(v) => setWeatherTemp(v.toString())}
                  />
                </div>
                <div>° Celsius / Gemiddeld </div>
                <div className={"w-32"}>
                  <Input
                    type={"number"}
                    label={""}
                    value={parseFloat(weatherSpeed)}
                    onChange={(v) => setWeatherSpeed(v.toString())}
                  />
                </div>
                <div>m/s windsnelheid (+</div>
                <div className={"w-32"}>
                  <Input
                    type={"number"}
                    label={""}
                    value={parseInt(weatherMeasureHeight)}
                    onChange={(v) => setWeatherMeasureHeight(v.toString())}
                  />
                </div>
                <div>m. boven maaiveld)</div>
              </div>
            </td>
          </tr>
          {loadCapacities.map((capacity, index) => {
            return (
              <HijslastRow
                key={index}
                loadCapacity={capacity}
                index={index + 1}
                onChange={(updatedCapacity) =>
                  setLoadCapacities((prev) => prev.map((c, i) => (i === index ? updatedCapacity : c)))
                }
                onRemove={() => {
                  setLoadCapacities((prev) => prev.filter((_, i) => i !== index));
                }}
              />
            );
          })}
          <tr>
            <td className={"font-medium pr-3 py-3 align-top"}></td>
            <td>
              <Button
                type={"secondary"}
                icon={faPlus}
                size={"sm"}
                text={"nieuwe hijslast"}
                onClick={() => {
                  setLoadCapacities([...loadCapacities, new LoadCapacity("", "", "", "")]);
                }}
              />
            </td>
          </tr>

          {liftingEquipments.map((equipment, index) => {
            return (
              <LiftingEquipmentRow
                key={index}
                liftingEquipment={equipment}
                index={index + 1}
                onChange={(updatedEquipment) =>
                  setLiftingEquipments((prev) => prev.map((c, i) => (i === index ? updatedEquipment : c)))
                }
                onRemove={() => {
                  setLiftingEquipments((prev) => prev.filter((_, i) => i !== index));
                }}
              />
            );
          })}
          <tr>
            <td className={"font-medium pr-3 py-3 align-top"}></td>
            <td>
              <Button
                type={"secondary"}
                icon={faPlus}
                size={"sm"}
                text={"nieuwe materieel"}
                onClick={() => {
                  setLiftingEquipments([...liftingEquipments, new LiftingEquipment("", "", "-", "", "")]);
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <ModalFooter text={"Opslaan"} onSubmit={save} />
    </div>
  );
};

const HijslastRow: FC<{
  loadCapacity: LoadCapacity;
  index: number;
  onChange: (updatedCapacity: LoadCapacity) => void;
  onRemove: () => void;
}> = (props) => {
  return (
    <tr>
      <td className={"font-medium pr-3 py-3 align-top"}>Hijslast {props.index}:</td>
      <td className={"pb-6"}>
        <div className={"flex flex-wrap items-center space-x-2 text-sm font-medium text-brand-800"}>
          <div className={"w-32"}>
            <Input
              type={"text"}
              label={""}
              value={props.loadCapacity.hijslastNaam ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.loadCapacity,
                  hijslastNaam: value,
                })
              }
            />
          </div>
          <div> / E.G. </div>
          <div className={"w-32"}>
            <Input
              type={"text"}
              label={""}
              value={props.loadCapacity.hijslastEg ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.loadCapacity,
                  hijslastEg: value,
                })
              }
            />
          </div>
          <div>T. excl. </div>
          <div className={"w-32"}>
            <Input
              type={"text"}
              label={""}
              value={props.loadCapacity.hijslastContingency ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.loadCapacity,
                  hijslastContingency: value,
                })
              }
            />
          </div>
          <div> % contingency</div>
          <div> / projectievlak hijslast (AP-waarde) </div>
          <div className={"w-32"}>
            <Input
              type={"text"}
              label={""}
              value={props.loadCapacity.hijslastProjectieVlak ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.loadCapacity,
                  hijslastProjectieVlak: value,
                })
              }
            />
          </div>
          <div>m² (optioneel)</div>
        </div>
      </td>
      <td>
        <IconButton size={"md"} icon={faCircleMinus} type={"danger"} onClick={() => props.onRemove()} />
      </td>
    </tr>
  );
};

const LiftingEquipmentRow: FC<{
  liftingEquipment: LiftingEquipment;
  index: number;
  onChange: (updatedEquipment: LiftingEquipment) => void;
  onRemove: () => void;
}> = (props) => {
  return (
    <tr>
      <td className={"font-medium pr-3 py-3 align-top"}>Materieel {props.index}:</td>
      <td className={"pb-6"}>
        <div className={"flex flex-wrap items-center space-x-2 text-sm font-medium text-brand-800"}>
          <div className={"w-32"}>
            <Input
              placeholder={"Amount"}
              type={"text"}
              label={""}
              value={props.liftingEquipment.materieelAmount ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.liftingEquipment,
                  materieelAmount: value,
                })
              }
            />
          </div>
          <div> x </div>
          <div className={"w-32"}>
            <Input
              placeholder={"Tonnage/Afmeting"}
              type={"text"}
              label={""}
              value={props.liftingEquipment.materieelTonnage ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.liftingEquipment,
                  materieelTonnage: value,
                })
              }
            />
          </div>
          <div>
            {" "}
            {getLiftingEquipmentSizeUnitByType(
              props.liftingEquipment.materieelType as keyof typeof liftingEquipmentTypeOptions,
            )}{" "}
          </div>
          <div className={"w-60"}>
            <Select
              label={""}
              options={liftingEquipmentTypeOptions}
              value={props.liftingEquipment.materieelType ?? "-"}
              onChange={(value) =>
                props.onChange({
                  ...props.liftingEquipment,
                  materieelType: value,
                })
              }
            />
          </div>
          <div className={"w-44"}>
            <Input
              placeholder={"Brand"}
              type={"text"}
              label={""}
              value={props.liftingEquipment.materieelBrand ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.liftingEquipment,
                  materieelBrand: value,
                })
              }
            />
          </div>
          <div>, </div>
          <div className={"w-44"}>
            <Input
              placeholder={"Model (optioneel)"}
              type={"text"}
              label={""}
              value={props.liftingEquipment.materieelModel ?? ""}
              onChange={(value) =>
                props.onChange({
                  ...props.liftingEquipment,
                  materieelModel: value,
                })
              }
            />
          </div>
        </div>
      </td>
      <td>
        <IconButton size={"md"} icon={faCircleMinus} type={"danger"} onClick={() => props.onRemove()} />
      </td>
    </tr>
  );
};
